import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Dispatch } from 'redux';
import { GIButton } from 'app-components';
import { FormattedMessage, useIntl } from 'react-intl';

import { DATE_FORMAT, formatDateToMMDDYYYY } from '../../../utils/dates/dateMethods';
import { useAppSelector, useAppDispatch } from '../../../hooks';
import { useIssuerList } from '../hooks/useAdminSerff';
import {
    fetchSerffPlanLoadStatusDataList,
    setAdminSerffIssuersOptions,
} from '../../../actions/admin/serff/serffPlansLoadStatusActionCreators';
import PaginatedTable, { renderCellContent } from '../../../components/common/table/PaginatedTable';
import ReactDatepicker from '../../../lib/inputs/react-datepicker';
import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import AdminSerffModal, { HandleModalContent } from './AdminSerffModal';
import Dropdown from '../../../lib/inputs/Dropdown';
import { IFormData } from '../types/adminSerffStatus.interface';
import { INPUT_TYPES } from '../../../components/common/form/constants/fieldTypes';
import { ADMIN_SERFF_PLAN_LOAD_STATUS_ACTION_TYPES } from '../../../constants/actionTypes/adminSerffStatusActionTypes';
import { getPlanLoadStatusFilters, getPlanLoadTypeFilters } from '../constants/enum';

const StyledContainer = styled.div`
    margin: 20px 0px;

    .tag-dropdown-label {
        padding-right: 5rem;
        padding-left: 0;
    }

    .table thead th {
        padding: 0;
    }

    .table thead th span {
        color: #444;
        font-size: 13px;
    }
`;

const StyledGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: auto;
    gap: 16px 8px;
    & > div:nth-child(n + 4) {
        grid-column: span 1;
    }
    padding: 2rem 1rem;
`;

const StyledButton = styled.div`
    padding: 2rem 0;
    display: flex;
    
    button {
        margin-left: 1rem;
    }
`;

const StyledField = styled.div`
    gap: 20px;
    
    > div {
        display: flex;
        flex-direction: column;
        align-items: start;
        margin: 0;
    }

    .gi-input {
        text-align: left;
    }
    .gi-input__label {
        padding: 0;
    }

    input,
    select {
        width: 80%;
    }

    .react-datepicker-wrapper {
        width: 80%;
    }

    .react-datepicker__input-container {
        input {
            width: 100%;
        }
    }
`;

const StyledLabel = styled.label`
    cursor: pointer;
    color: ${({ theme }) => theme.primaryMain};
`;

const AdminSerffPlansLoadStatusContainer = () => {
    const intl = useIntl();

    const typeOptions = React.useMemo(() => getPlanLoadTypeFilters(intl), [intl]);
    const statusOptions = React.useMemo(() => getPlanLoadStatusFilters(intl), [intl]);
    const resetValues = {
        issuerFilter: '',
        typeFilter: '',
        statusFilter: '',
        dateFilter: '',
    };

    const dispatch: Dispatch<any> = useAppDispatch();
    const { issuerDropdownOptions } = useIssuerList(intl, 'form.option.select');

    const { planLoadStatusData } = useAppSelector(state => state.adminPortal.adminSerffPlansLoadStatus);
    const { filters } = useAppSelector(state => state.adminPortal.adminSerffIsserList);

    const {
        content = [],
        pageable: { pageNumber = 0, pageSize = 10 } = {},
        totalPages = 0,
        totalElements = 0,
        first = false,
    } = planLoadStatusData;

    const { issuers = [] } = filters;
    const [currentPage, setCurrentPage] = useState(pageNumber);
    const [currentPageSize, setPageSize] = useState(pageSize);
    const [formData, setFormData] = useState<IFormData>(resetValues);
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState({
        title: 'admin.serff.plansLoadStatus.modal.title',
        content: content,
        type: 'planLoadLogs',
    });

    useEffect(() => {
        dispatch(setAdminSerffIssuersOptions(issuerDropdownOptions));
    }, [dispatch, issuerDropdownOptions]);
    
    useEffect(() => {
        dispatch(fetchSerffPlanLoadStatusDataList({ ...resetValues, page: currentPage, size: currentPageSize }));
    }, []);

    const handleDropdownChange = (fieldKey: string, value: string) => {
        setFormData(prevFormData => ({
            ...prevFormData,
            [fieldKey]: value || '',
        }));
    };

    const onUpdateDateFields = (value: Date | null) => {
        const formattedDate = formatDateToMMDDYYYY(value);
        setFormData(prevFormData => ({
            ...prevFormData,
            dateFilter: formattedDate,
        }));
    };

    const handleSubmit = event => {
        event.preventDefault();
        setCurrentPage(0);
        dispatch({
            type: ADMIN_SERFF_PLAN_LOAD_STATUS_ACTION_TYPES.SET_FILTERS,
            payload: {
                issuerFilter: formData.issuerFilter,
                typeFilter: formData.typeFilter,
                statusFilter: formData.statusFilter,
                dateFilter: formData.dateFilter,
            },
        });
        dispatch(
            fetchSerffPlanLoadStatusDataList({
                issuerFilter: formData.issuerFilter,
                typeFilter: formData.typeFilter,
                statusFilter: formData.statusFilter,
                dateFilter: formData.dateFilter,
                page: 0,
                size: currentPageSize,
            }),
        );
    };
    const handleReset = event => {
        event.preventDefault();
        setCurrentPage(0);
        setFormData(resetValues);
        dispatch({
            type: ADMIN_SERFF_PLAN_LOAD_STATUS_ACTION_TYPES.SET_FILTERS,
            payload: resetValues,
        });
        dispatch(fetchSerffPlanLoadStatusDataList({ ...resetValues, page: currentPage, size: currentPageSize }));
    };

    const handlePageChange = page => {
        setCurrentPage(page);
        dispatch(
            fetchSerffPlanLoadStatusDataList({
                issuerFilter: formData.issuerFilter,
                typeFilter: formData.typeFilter,
                statusFilter: formData.statusFilter,
                dateFilter: formData.dateFilter,
                size: currentPageSize,
                page,
            }),
        );
    };

    const handlePageSizeChange = size => {
        setPageSize(size);
        dispatch(
            fetchSerffPlanLoadStatusDataList({
                issuerFilter: formData.issuerFilter,
                typeFilter: formData.typeFilter,
                statusFilter: formData.statusFilter,
                dateFilter: formData.dateFilter,
                page: currentPage,
                size,
            }),
        );
    };

    const handleModalContent = content => {
        setModalData(prev => ({ ...prev, content: content }));
    };

    const handleShowModal = content => {
        setModalData(() => ({
            title: 'admin.serff.plansLoadStatus.modal.title',
            content: content,
            type: 'planLoadLogs',
        }));
        setShowModal(!showModal);
    };

    const dropdownConfigs = [
        {
            id: 'issuerFilter',
            labelId: 'admin.serff.plansLoadStatus.dropdown.issuer',
            options: issuers,
            value: formData.issuerFilter,
        },
        {
            id: 'typeFilter',
            labelId: 'admin.serff.plansLoadStatus.dropdown.type',
            options: typeOptions,
            value: formData.typeFilter,
        },
        {
            id: 'statusFilter',
            labelId: 'admin.serff.plansLoadStatus.dropdown.status',
            options: statusOptions,
            value: formData.statusFilter,
        },
    ];

    const columns = [
        {
            key: 'issuerName',
            label: <FormattedMessage id='admin.serff.plansLoadStatus.columns.issuerName' />,
            render: (item: any) => renderCellContent(item?.issuerName, { width: '200px' }),
        },
        {
            key: 'hiosIssuerID',
            label: <FormattedMessage id='admin.serff.plansLoadStatus.columns.hiosIssuerID'/>,
            render: (item: any) => renderCellContent(item?.hiosIssuerID),
        },
        {
            key: 'uploadedDate',
            label: <FormattedMessage id='admin.serff.plansLoadStatus.columns.uploadedDate'/>,
            render: (item: any) => renderCellContent(item?.uploadedDate),
        },
        {
            key: 'type',
            label: <FormattedMessage id='admin.serff.plansLoadStatus.columns.type'/>,
            render: (item: any) => renderCellContent(item?.type),
        },
        {
            key: 'status',
            label: <FormattedMessage id='admin.serff.plansLoadStatus.columns.status'/>,
            render: (item: any) => renderCellContent(item?.status, { width: '100px' }),
        },
        {
            key: 'description',
            label: <FormattedMessage id='admin.serff.plansLoadStatus.columns.description'/>,
            render: (item: any) => renderCellContent(item?.description, { width: '180px' }),
        },
        {
            key: 'logs',
            label: <FormattedMessage id='admin.serff.plansLoadStatus.columns.logs'/>,
            render: (item: any) => (
                <StyledLabel onClick={() => handleShowModal(item?.logs || '')}>
                    {renderCellContent('Logs', { width: '70px' })}
                </StyledLabel>
            ),
            filterOptions: ['HIDE_COLUMN']
        },
        {
            key: 'differenceReport',
            label: <FormattedMessage id='admin.serff.plansLoadStatus.columns.differenceReport'/>,
            render: (item: any) => {
                if (!item?.differenceReport) return null;

                const handleClick = () => {
                    const selectedDocumentId = item?.differenceReport;
                    if (selectedDocumentId) {
                        window.location.href = `/hix/admin/serff/downloadSerffTemplate?selectedDocumentId=${selectedDocumentId}`;
                    }
                };

                return <StyledLabel onClick={handleClick}>Download</StyledLabel>;
            },
            filterOptions: ['HIDE_COLUMN']
        },
    ];

    return (
        <StyledContainer data-testid="admin-serff-planload-container">
            <FormattedMessage id={'admin.serff.plansLoadStatus'} tagName="h1" data-testid="planload-header" />
            <form
                onSubmit={e => handleSubmit(e)}
                className="form-group"
                data-testid="plan-load-status-form"
            >
                <StyledGrid data-testid="form-grid">
                    {dropdownConfigs?.map(({ id, labelId, options, value }) => (
                        <StyledField key={id} data-testid={`dropdown-wrapper-${id}`}>
                            <Dropdown
                                label={{
                                    text: <FormattedMessage id={labelId} />,
                                    labelClass: ['tag-dropdown-label'],
                                }}
                                inputData={{
                                    options,
                                    type: INPUT_TYPES.DROPDOWN,
                                    value,
                                    isInputRequired: false,
                                }}
                                inputActions={{
                                    handleFieldChange: value => handleDropdownChange(id, value),
                                }}
                                data-testid={`dropdown-${id}`}
                            />
                        </StyledField>
                    ))}
                    <StyledField data-testid="datepicker-wrapper-dateFilter">
                        <ReactDatepicker
                            labelText="common.date"
                            wrapperClasses={['date-picker']}
                            id="adminSerffPlansLoadStatusdDateUI"
                            type="text"
                            pattern={DATE_FORMAT}
                            disabled={false}
                            value={formData?.dateFilter ? new Date(formData.dateFilter) : null}
                            withMask
                            onDateChange={value => onUpdateDateFields(value)}
                            data-testid="datepicker-dateFilter"
                        />
                    </StyledField>
                </StyledGrid>

                <StyledButton data-testid="form-buttons-wrapper">
                    <GIButton
                        id="submit_btn"
                        labelId="screenpop.button.go"
                        isDisabled={false}
                        type="submit"
                        data-testid="submit-button"
                    />
                    <GIButton
                        id="reset_btn"
                        labelId="button.reset"
                        isDisabled={false}
                        type="reset"
                        variant="secondary"
                        onClickHandler={e => handleReset(e)}
                        data-testid="reset-button"
                    />
                </StyledButton>
            </form>

            <PaginatedTable
                data={content}
                columns={columns}
                pageSize={currentPageSize}
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalElements}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                startPageIndex={first ? pageNumber : 0}
                hidePageControl={false}
            />

            <ConditionalRendering isTrue={showModal}>
                <AdminSerffModal
                    modalTitleId={modalData?.title || 'admin.serff.plansLoadStatus.modal.title'}
                    closeModal={() => {
                        setShowModal(false);
                    }}
                    content={
                        <HandleModalContent
                            modaltype={modalData?.type}
                            modalContent={modalData?.content || ''}
                            handleModalContent={handleModalContent}
                        />
                    }
                    data-testid="serff-planload-modal"
                />
            </ConditionalRendering>
        </StyledContainer>
    );
};

export default AdminSerffPlansLoadStatusContainer;
